import { Component } from '@theme/component';

/**
 * @typedef {object} SmsSignupRefs
 * @property {HTMLFormElement} form - The signup form.
 * @property {HTMLFieldSetElement} fields - The fieldset holding the visible controls.
 * @property {HTMLInputElement} timezone - The hidden timezone input.
 * @property {HTMLElement} status - The live region for the submission result.
 */

/**
 * Posts the email and SMS signup to the form's endpoint without leaving the page.
 * @extends Component<SmsSignupRefs>
 */
class SmsSignupComponent extends Component {
  requiredRefs = ['form', 'fields', 'timezone', 'status'];

  /**
   * @param {SubmitEvent} event
   */
  async submit(event) {
    event.preventDefault();

    const { form, fields, timezone, status } = this.refs;

    if (fields.disabled) return;

    timezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // A disabled fieldset drops its controls from FormData, so read the form first.
    const formData = new FormData(form);

    fields.disabled = true;
    status.textContent = '';

    try {
      const response = await fetch(form.action, { method: 'POST', body: formData });
      const result = await response.json();

      if (!response.ok || result.success === '') throw new Error(`Signup rejected with status ${response.status}`);

      status.textContent = this.dataset.successMessage ?? '';
      /** @type {any} */ (window).Northbeam?.fireCustomGoal?.('sms_sign_up', {});
    } catch (error) {
      console.error(error);
      status.textContent = this.dataset.errorMessage ?? '';
      fields.disabled = false;
    }
  }

  /**
   * Rewrites a complete US number as (555) 555-5555 once the field loses focus.
   * @param {FocusEvent} event
   */
  formatPhone(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const digits = input.value.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

    if (digits.length !== 10) return;

    input.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
}

if (!customElements.get('sms-signup-component')) {
  customElements.define('sms-signup-component', SmsSignupComponent);
}
